import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Share2, X, ArrowRight } from 'lucide-react'

// Bump the version suffix to re-announce a future feature.
const STORAGE_KEY = 'avi_shared_reports_banner_v1'

/**
 * One-time dashboard banner announcing the Shared Reports feature. Persists dismissal in
 * localStorage so it shows until the user closes it (the X), then never again on this browser.
 */
const SharedReportsBanner: React.FC = () => {
  const navigate = useNavigate()
  const [show, setShow] = useState<boolean>(() => {
    try { return !localStorage.getItem(STORAGE_KEY) } catch { return true }
  })

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, '1') } catch { /* localStorage unavailable */ }
    setShow(false)
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.25 }}
          className="overflow-hidden"
        >
          <div className="relative overflow-hidden rounded-xl border border-indigo-300/50 bg-gradient-to-r from-indigo-600 to-violet-600 text-white shadow-sm">
            <div className="flex items-center gap-4 px-4 py-3 sm:px-5">
              <div className="h-10 w-10 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0">
                <Share2 className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">New</span>
                  <h3 className="text-sm font-bold">Shared Reports</h3>
                </div>
                <p className="text-xs text-indigo-100 mt-0.5">
                  Save a report as a snapshot and share it with a secure link — recipients view it online or download the PDF, no login required.
                </p>
              </div>
              <button
                onClick={() => navigate('/reports')}
                className="hidden sm:inline-flex items-center gap-1.5 flex-shrink-0 rounded-lg bg-white px-3.5 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 transition-colors"
              >
                Try it <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={dismiss}
                title="Dismiss"
                aria-label="Dismiss"
                className="flex-shrink-0 text-indigo-100 hover:text-white transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default SharedReportsBanner
